import {
  ApprovalAuthorization,
  ApprovalRequestNotification,
  ApprovalRequestStatus,
} from '@agilelab/plugin-wb-notification-common';
import {
  customAlertApiRef,
  ProgressButton,
  useUsername,
  useUserProfile,
  WbAvatar,
} from '@agilelab/plugin-wb-platform';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import { Box, Typography, useTheme } from '@material-ui/core';
import DOMPurify from 'dompurify';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { notificationApiRef } from '../../notificationApiRef';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureAction,
  NotificationDisclosureSummary,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import { useNotification } from './useNotificationProvider';
import logo from './witboost_logo.svg';

export const ApprovalNotificationDisclosure = (props: {
  notification: ApprovalRequestNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const theme = useTheme();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const notificationCatalog = useApi(notificationApiRef);
  const queryClient = useQueryClient();
  const { setRead } = useNotification();
  const { profile } = useUserProfile();
  const loggedUsername = useUsername();
  const [loading, setLoading] = useState<'approve' | 'reject' | ''>('');

  const approvalResponse =
    notification.notification_response as ApprovalAuthorization | null;

  const onToggle = useCallback(
    (_event: object, expanded: boolean) => {
      onAccordionExpansionsChange(
        expanded ? notification.id.toString() : false,
      );
    },
    [notification.id, onAccordionExpansionsChange],
  );

  const requestTransition = useCallback(
    async (action: 'approve' | 'reject') => {
      setLoading(action);
      try {
        await notificationCatalog.updateNotificationResponse(
          notification.id.toString(),
          {
            status:
              action === 'approve'
                ? ApprovalRequestStatus.ACCEPT
                : ApprovalRequestStatus.REJECT,
            timestamp: new Date().toJSON(),
            resolved_by: loggedUsername?.username || profile.email,
            resolved_by_display_name:
              loggedUsername?.displayName || profile.displayName,
          },
          await identityApi.getCredentials(),
        );

        alertApi.post({
          message:
            action === 'approve'
              ? 'Request approved successfully.'
              : 'Request rejected.',
          severity: 'success',
          timeoutMillis: 5000,
        });
        queryClient.invalidateQueries({
          queryKey: ['paginatedByRecipient'],
        });
        setRead(notification.id.toString());
      } catch (error: any) {
        alertApi.post({
          error,
          severity: 'error',
          timeoutMillis: 5000,
        });
      } finally {
        setLoading('');
      }
    },
    [
      alertApi,
      identityApi,
      loggedUsername?.displayName,
      loggedUsername?.username,
      notification.id,
      notificationCatalog,
      profile.displayName,
      profile.email,
      queryClient,
      setRead,
    ],
  );

  const showButtons = !approvalResponse;

  return (
    <NotificationDisclosure
      onChange={onToggle}
      expanded={expandedAccordion === notification.id.toString()}
      style={{
        backgroundColor: notification.read_at
          ? 'transparent'
          : theme.palette.common.white,
      }}
    >
      <NotificationDisclosureSummary expandIcon={<ExpandMoreIcon />}>
        <div className={classes.summary}>
          <Box>
            <div className={classes.summaryHeader}>
              <Typography className={classes.notificationTime}>
                {DateTime.fromISO(notification.created_at).toFormat(
                  'dd/MM/yyyy HH:mm',
                )}
              </Typography>
              {!approvalResponse && !notification.read_at && (
                <ErrorOutlineOutlinedIcon fontSize="small" color="primary" />
              )}
              {approvalResponse?.status === ApprovalRequestStatus.ACCEPT && (
                <CheckCircleOutlineOutlinedIcon
                  fontSize="small"
                  style={{ color: theme.palette.success.main }}
                />
              )}
              {approvalResponse?.status === ApprovalRequestStatus.REJECT && (
                <NotInterestedIcon
                  fontSize="small"
                  style={{ color: theme.palette.error.main }}
                />
              )}
            </div>
            <div className={classes.summaryDescription}>
              <WbAvatar
                displayName={
                  notification.sender ??
                  configApi.getOptionalString('app.title') ??
                  'Witboost'
                }
                picture={logo}
                classes={{ avatar: classes.avatar }}
              />
              <div className={classes.summaryDescriptionText}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      notification.notification_request.text,
                    ),
                  }}
                />
              </div>
            </div>
          </Box>
          <Box alignSelf="center">
            <MarkAsReadButton notification={notification} />
          </Box>
        </div>
      </NotificationDisclosureSummary>
      <NotificationDisclosureAction>
        {showButtons && (
          <>
            <ProgressButton
              meshColor="red"
              variant="contained"
              inProgress={loading === 'reject'}
              disabled={loading === 'approve'}
              onClick={() => requestTransition('reject')}
            >
              Reject
            </ProgressButton>
            <ProgressButton
              meshColor="green"
              variant="contained"
              inProgress={loading === 'approve'}
              disabled={loading === 'reject'}
              onClick={() => requestTransition('approve')}
            >
              Approve
            </ProgressButton>
          </>
        )}
        {approvalResponse?.status === ApprovalRequestStatus.ACCEPT && (
          <Typography style={{ color: theme.palette.success.main }}>
            Approved
          </Typography>
        )}
        {approvalResponse?.status === ApprovalRequestStatus.REJECT && (
          <Typography style={{ color: theme.palette.error.main }}>
            Rejected
          </Typography>
        )}
      </NotificationDisclosureAction>
    </NotificationDisclosure>
  );
};
